import * as React from 'react';
import { ScopedInteractionProvider } from '@/components/interaction/interaction-scope';
import { useAgentNativePrefillBoundary } from '@/components/agent/agent-native-prefill';
import { DocumentSaveReviewController } from '@/components/files/document-save-review';
import { ElevationBanner } from '@/components/access/elevation-banner';
import { SupportAccessBanner } from '@/components/access/temporary-access';
import { Outlet, useLocation } from 'react-router-dom';
import { LoaderCircleIcon } from 'lucide-react';
import { BrandMark } from '@/components/layout/brand-mark';
import { RememberLastPage } from '@/components/routing/last-visited-page';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/layout/header';
import { ManageModeProvider } from '@/components/manage/manage-mode';
import { Sidebar } from '@/components/layout/sidebar';
import { tenantAdminNavigation } from '@/lib/navigation';
import { useSession } from '@/components/auth/user-provider';
import { SignInRecovery } from '@/components/auth/sign-in-recovery';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { AiContextProvider } from '@/components/focus-queue/ai-context';
import { CommandPalette } from '@/components/focus-queue/command-palette';

const SIDEBAR_STORAGE_KEY = 'cdf-sidebar-collapsed';

function readSidebarPreference(storageKey: string | null): boolean {
  if (!storageKey) return true;
  try {
    const stored = localStorage.getItem(storageKey);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

function saveSidebarPreference(storageKey: string | null, collapsed: boolean) {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, String(collapsed));
  } catch {
    // The current session still keeps the selected state if storage is unavailable.
  }
}

function SignedOutScreen() {
  const { authIssue, isSigningIn, recoveryUrl, signIn } = useSession();

  return (
    <div className="bg-background flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <BrandMark className="mx-auto size-14 rounded-2xl" />
          <CardTitle>Relationship Foundry</CardTitle>
          <CardDescription>You have been signed out.</CardDescription>
        </CardHeader>
        <CardContent>
          <SignInRecovery
            issue={authIssue}
            isSigningIn={isSigningIn}
            onSignIn={signIn}
            recoveryUrl={recoveryUrl}
            signInLabel="Sign in"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function AuthLoadingScreen() {
  return (
    <div
      className="bg-background text-muted-foreground flex min-h-svh items-center justify-center gap-2 p-4 text-sm"
      role="status"
      aria-live="polite"
    >
      <LoaderCircleIcon className="size-4 animate-spin" aria-hidden />
      Checking your Microsoft session…
    </div>
  );
}

export function AppShell() {
  useAgentNativePrefillBoundary();
  const { session } = useSession();
  const access = useTenantEntity();
  const { pathname } = useLocation();
  const sidebarStorageKey = session.status === 'signedIn'
    ? `${SIDEBAR_STORAGE_KEY}:${(session.user.email || session.user.name).toLowerCase()}`
    : null;
  const [collapsed, setCollapsed] = React.useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    setCollapsed(readSidebarPreference(sidebarStorageKey));
  }, [sidebarStorageKey]);

  const toggleSidebar = () => {
    setCollapsed((value) => {
      const next = !value;
      saveSidebarPreference(sidebarStorageKey, next);
      return next;
    });
  };

  if (session.status === 'loading') {
    return <AuthLoadingScreen />;
  }

  if (session.status === 'signedOut') {
    return <SignedOutScreen />;
  }

  const requiresEntity = session.user.role !== 'customer';
  const accessReady = !requiresEntity || access.status === 'ready';

  const scopedContent = (() => {
    if (!requiresEntity || ['/admin/platform', '/admin/tenant', '/admin/entity'].includes(pathname)) return <Outlet />;
    if (access.status === 'loading' || access.status === 'switching') {
      return (
        <div className="text-muted-foreground flex min-h-48 items-center justify-center gap-2 text-sm" role="status" aria-live="polite">
          <LoaderCircleIcon className="size-4 animate-spin" aria-hidden />
          {access.status === 'switching' ? 'Loading selected entity…' : 'Loading your access…'}
        </div>
      );
    }
    if (access.status === 'noAccess') {
      return (
        <Card className="mx-auto mt-8 w-full max-w-lg">
          <CardHeader>
            <CardTitle>No entity access</CardTitle>
            <CardDescription>
              Your account is signed in, but it does not currently have an active entity grant.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => void access.refresh()}>Check again</Button>
          </CardContent>
        </Card>
      );
    }
    if (access.status === 'error') {
      return (
        <Card className="mx-auto mt-8 w-full max-w-lg" role="alert">
          <CardHeader>
            <CardTitle>Entity access unavailable</CardTitle>
            <CardDescription>{access.error ?? 'The access service could not be reached.'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => void access.refresh()}>Try again</Button>
          </CardContent>
        </Card>
      );
    }
    if (access.status !== 'ready') return null;
    return <React.Fragment key={`${access.selected?.tenantId}:${access.selected?.entityId}`}><Outlet /></React.Fragment>;
  })();

  return (
    <AiContextProvider>
    <ScopedInteractionProvider>
    <ManageModeProvider>
    <div className="bg-background flex h-svh flex-col">
      {/* Skip link for keyboard users (WCAG 2.4.1) — revealed on focus */}
      <a
        href="#main-content"
        className="bg-background text-foreground ring-ring sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:px-3 focus:py-2 focus:text-sm focus:ring-2"
      >
        Skip to main content
      </a>
      {/* Full-width sticky header — stays put while the content scrolls */}
      <Header
        onMobileSidebarOpen={() => setMobileSidebarOpen(true)}
        showAgent={accessReady}
      />
      <RememberLastPage />
      <ElevationBanner />
      <SupportAccessBanner />
      <DocumentSaveReviewController />
      <div className="flex min-h-0 flex-1">
        {/* Sidebar: desktop inline panel, mobile slide-in overlay */}
        {accessReady || access.platformPermissions.length > 0 || access.tenants.some((tenant) => tenantAdminNavigation.items.some(item => item.permissions?.some(key => tenant.permissions?.includes(key)))) ? (
          <Sidebar
            collapsed={collapsed}
            onToggle={toggleSidebar}
            mobileOpen={mobileSidebarOpen}
            onMobileClose={() => setMobileSidebarOpen(false)}
          />
        ) : null}
        <main
          id="main-content"
          tabIndex={-1}
          className={pathname === '/agent' ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-2 outline-none md:p-3' : 'flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-4 outline-none md:p-6'}
        >
          {scopedContent}
        </main>
      </div>
      {accessReady ? <CommandPalette /> : null}
    </div>
    </ManageModeProvider>
    </ScopedInteractionProvider>
    </AiContextProvider>
  );
}
